import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #level2Handlers = new WeakMap();
  #level2Observers = new WeakMap();
  /** @type {string | null} */
  #originalHeaderState = null;
  /** @type {HTMLElement | null} */
  #aside = null;
  /** @type {HTMLElement | null} */
  #freeShippingBanner = null;
  /** @type {MutationObserver | null} */
  #freeShippingBannerObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupLevel2Dropdowns();
    this.#setupAsideVisibilityTracking();
    this.#setupFreeShippingBannerVisibilityTracking();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#cleanupLevel2Dropdowns();
    window.removeEventListener('scroll', this.#updateAsideVisibleHeight);
    window.removeEventListener('scroll', this.#updateFreeShippingBannerVisibleHeight);
    this.#freeShippingBannerObserver?.disconnect();
  }

  /**
   * Setup tracking for aside (announcement bar) visible height in viewport
   */
  #setupAsideVisibilityTracking() {
    this.#aside = document.querySelector('.shopify-section-group-header-group aside');
    if (!this.#aside) return;

    // Initial calculation
    this.#updateAsideVisibleHeight();

    // Update on scroll
    window.addEventListener('scroll', this.#updateAsideVisibleHeight, { passive: true });
  }

  /**
   * Calculate and update the visible height of aside in viewport
   */
  #updateAsideVisibleHeight = () => {
    if (!this.#aside) return;

    const rect = this.#aside.getBoundingClientRect();
    // Calculate how much of aside is visible in viewport
    // rect.bottom is the distance from top of viewport to bottom of aside
    // If rect.bottom <= 0, aside is fully scrolled out
    // If rect.bottom >= aside height, aside is fully visible
    const visibleHeight = Math.max(0, Math.min(rect.bottom, rect.height));
    document.body.style.setProperty('--aside-visible-height', `${visibleHeight}px`);
  };

  /**
   * Setup tracking for free shipping banner visible height in viewport
   */
  #setupFreeShippingBannerVisibilityTracking() {
    this.#freeShippingBanner = document.getElementById('FreeShippingBanner');

    if (this.#freeShippingBanner) {
      this.#initFreeShippingBannerTracking();
    } else {
      // Element is dynamically added to DOM, watch for it
      this.#freeShippingBannerObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLElement) {
              const banner = node.id === 'FreeShippingBanner' ? node : /** @type {HTMLElement | null} */ (node.querySelector('#FreeShippingBanner'));
              if (banner) {
                this.#freeShippingBanner = banner;
                this.#initFreeShippingBannerTracking();
                this.#freeShippingBannerObserver?.disconnect();
                return;
              }
            }
          }
        }
      });

      this.#freeShippingBannerObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Initialize free shipping banner tracking once element is available
   */
  #initFreeShippingBannerTracking() {
    // Initial calculation
    this.#updateFreeShippingBannerVisibleHeight();

    // Update on scroll
    window.addEventListener('scroll', this.#updateFreeShippingBannerVisibleHeight, { passive: true });
  }

  /**
   * Calculate and update the visible height of free shipping banner in viewport
   */
  #updateFreeShippingBannerVisibleHeight = () => {
    if (!this.#freeShippingBanner) return;

    const rect = this.#freeShippingBanner.getBoundingClientRect();
    // Calculate how much of banner is visible in viewport
    // rect.bottom is the distance from top of viewport to bottom of banner
    // If rect.bottom <= 0, banner is fully scrolled out
    // If rect.bottom >= banner height, banner is fully visible
    const visibleHeight = Math.max(0, Math.min(rect.bottom, rect.height));
    document.body.style.setProperty('--free-shipping-banner-visible-height', `${visibleHeight}px`);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Get the header component element
   * @returns {HTMLElement | null}
   */
  #getHeaderElement() {
    return this.closest('header-component') ?? document.querySelector('header-component');
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event) {
      event.preventDefault();
    }
    return this.isOpen ? this.close() : this.open(event);
  }
  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Only set 'open' attribute on the main menu drawer container
    if (details.classList.contains('menu-drawer-container') || details === this.refs.details) {
      const header = this.#getHeaderElement();
      if (header) {
        const currentState = header.dataset.headerState;
        if (currentState === 'transparent' && this.#originalHeaderState !== 'transparent' && !this.isOpen) {
          this.#originalHeaderState = 'transparent';
          header.dataset.headerState = 'solid';
        }
      }
      
      details.setAttribute('open', '');
    }
    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        if (this.#originalHeaderState === 'transparent') {
          const header = this.#getHeaderElement();
          if (header) {
            header.dataset.headerState = 'transparent';
            this.#originalHeaderState = null;
          }
        }
        
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Cleanup level 2 dropdown event listeners and observers
   */
  #cleanupLevel2Dropdowns() {
    const level2Details = this.querySelectorAll('.menu-drawer__submenu-container');
    
    level2Details.forEach((details) => {
      const summary = details.querySelector('.menu-drawer__menu-item--level2');
      if (!summary) return;

      // Remove event listener
      const handler = this.#level2Handlers.get(summary);
      if (handler) {
        summary.removeEventListener('click', handler);
        this.#level2Handlers.delete(summary);
      }

      // Disconnect observer
      const observer = this.#level2Observers.get(details);
      if (observer) {
        observer.disconnect();
        this.#level2Observers.delete(details);
      }
    });
  }

  /**
   * Setup level 2 dropdown functionality
   * Prevents default summary behavior and handles dropdown toggle
   */
  #setupLevel2Dropdowns() {
    const level2Details = this.querySelectorAll('.menu-drawer__submenu-container');
    
    level2Details.forEach((details) => {
      const summary = details.querySelector('.menu-drawer__menu-item--level2');
      if (!summary) return;

      // Remove existing event listener if any
      const existingHandler = this.#level2Handlers.get(summary);
      if (existingHandler) {
        summary.removeEventListener('click', existingHandler);
      }

      // Remove existing observer if any
      const existingObserver = this.#level2Observers.get(details);
      if (existingObserver) {
        existingObserver.disconnect();
      }

      // Handle summary click - toggle dropdown instead of navigating
      /**
       * @param {MouseEvent} event
       */
      const clickHandler = (event) => {
        // Don't toggle if clicking on the hidden link (for keyboard navigation)
        if (event.target instanceof Element && event.target.closest('.menu-drawer__level2-link')) {
          return; // Allow link navigation for keyboard users
        }

        // Prevent default summary behavior (which would toggle immediately)
        event.preventDefault();
        event.stopPropagation();
        
        // Manually toggle the details element
        // aria-expanded will be synced by MutationObserver below
        const isOpen = details.hasAttribute('open');
        if (isOpen) {
          details.removeAttribute('open');
        } else {
          details.setAttribute('open', '');
        }
      };

      summary.addEventListener('click', clickHandler);
      this.#level2Handlers.set(summary, clickHandler);

      // Sync aria-expanded with details open state
      const observer = new MutationObserver(() => {
        const isOpen = details.hasAttribute('open');
        summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });

      observer.observe(details, {
        attributes: true,
        attributeFilter: ['open']
      });
      this.#level2Observers.set(details, observer);

      // Set initial aria-expanded
      summary.setAttribute('aria-expanded', details.hasAttribute('open') ? 'true' : 'false');
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
